import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

/** Panel container: rounded-xl, hairline border, very subtle shadow. */
export const Card = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function Card({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-border bg-surface shadow-sm',
        className
      )}
      {...props}
    />
  );
});

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Controls aligned to the end of the header row (buttons, badges). */
  actions?: ReactNode;
}

/** Title row. Put `CardTitle` and optional `CardDescription` inside. */
export const CardHeader = ({
  className,
  actions,
  children,
  ...props
}: CardHeaderProps) => (
  <div
    className={cn(
      'flex items-start justify-between gap-3 px-4 pb-0 pt-4 sm:px-5',
      className
    )}
    {...props}
  >
    <div className="min-w-0 flex-1 space-y-1">{children}</div>
    {actions != null && (
      <div className="flex shrink-0 items-center gap-2">{actions}</div>
    )}
  </div>
);

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Heading level for document outline. Defaults to `h3`. */
  as?: 'h2' | 'h3' | 'h4';
}

export const CardTitle = ({
  as: Heading = 'h3',
  className,
  ...props
}: CardTitleProps) => (
  <Heading
    className={cn('text-sm font-semibold leading-6 text-fg', className)}
    {...props}
  />
);

export const CardDescription = ({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-fg-muted', className)} {...props} />
);

export const CardBody = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-4 py-4 sm:px-5', className)} {...props} />
);

/** Footer row for actions, separated by a hairline. Right-aligned. */
export const CardFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-wrap items-center justify-end gap-2 rounded-b-xl border-t border-border bg-surface-sunken px-4 py-3 sm:px-5',
      className
    )}
    {...props}
  />
);
